import React from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Box, Button, MenuItem, Paper, TextField, Typography } from '@mui/material';

import { parseDate, teacherName, timeLabel } from '@common/helpers/academyReworkings';

import { BookingFlow } from '../../hooks/useBookingFlow';
import { cardSx, nextButtonSx } from '../../styled';

export default function Tutors({ flow }: { flow: BookingFlow }) {
	const { t } = useTranslation();
	const times = [...flow.times].sort(
		(a, b) => parseDate(a.window.start).toMillis() - parseDate(b.window.start).toMillis(),
	);
	return (
		<Box display="flex" flexDirection="column" gap="16px">
			<Typography variant="h3">{t('academyReworkings.chooseTutors')}</Typography>
			<Typography color="text.secondary">{t('academyReworkings.tutorsHint')}</Typography>
			{times.map((time, index) => {
				const onlyTeacher = time.window.slots.length === 1 ? time.window.slots[0].teacher : null;
				return (
					<Paper
						key={time.window.key}
						sx={(theme) => ({
							...cardSx(theme),
							display: 'grid',
							gridTemplateColumns: 'minmax(0, 1fr)',
							gap: 2,
							alignItems: 'center',
							[theme.breakpoints.up('sm')]: {
								p: 3,
								gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
							},
						})}
					>
						<Box sx={{ minWidth: 0 }}>
							<Typography color="text.secondary" variant="body2">
								{parseDate(time.window.start).toFormat('ccc, dd.MM.yyyy')}
							</Typography>
							<Typography variant="h4">{timeLabel(time.window)}</Typography>
							<Typography sx={{ mt: 1 }}>{flow.pendingLessons[index]?.lesson_name}</Typography>
						</Box>
						<Box sx={{ minWidth: 0 }}>
							{onlyTeacher ? (
								<Box display="flex" alignItems="center" gap="12px" sx={{ minWidth: 0 }}>
									<Avatar sx={{ bgcolor: 'primary.main', flexShrink: 0 }}>
										{teacherName(onlyTeacher)
											.split(' ')
											.map((part) => part[0])
											.slice(0, 2)
											.join('')}
									</Avatar>
									<Typography fontWeight={600} sx={{ minWidth: 0 }}>
										{teacherName(onlyTeacher)}
									</Typography>
								</Box>
							) : (
								<TextField
									select
									fullWidth
									sx={{
										minWidth: 0,
										'& .MuiSelect-select': { whiteSpace: 'normal', overflowWrap: 'anywhere' },
									}}
									SelectProps={{
										MenuProps: {
											PaperProps: {
												sx: {
													maxWidth: 'calc(100vw - 32px)',
													'& .MuiMenuItem-root': { whiteSpace: 'normal', overflowWrap: 'anywhere' },
												},
											},
										},
									}}
									label={t('academyReworkings.tutor')}
									value={time.slotId ?? ''}
									onChange={(event) =>
										flow.selectTutor(time.window.key, Number(event.target.value))}
									error={flow.tutorsAttempted && !time.slotId}
									helperText={
										flow.tutorsAttempted && !time.slotId
											? t('academyReworkings.selectTutor')
											: undefined
									}
								>
									{time.window.slots.map((slot) => (
										<MenuItem key={slot.id} value={slot.id}>
											{teacherName(slot.teacher)}
										</MenuItem>
									))}
								</TextField>
							)}
						</Box>
					</Paper>
				);
			})}
			<Box display="flex" justifyContent="flex-end">
				<Button variant="contained" sx={nextButtonSx} onClick={flow.showConfirmation}>
					{t('academyReworkings.nextConfirm')}
				</Button>
			</Box>
		</Box>
	);
}
